import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import {
	MultiPrimaryAutocompleteField,
	MultiPrimaryItem
} from '@/components/Form/FormAutocompleteField/MultiPrimaryAutocompleteField';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import type { OrganizationalSectionValuesIn } from '@/features/DataChangeRequest/_schemas/HcoSchemas/OrganizationalSection.validation';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import { FormSection } from '@/features/DataChangeRequest/DcrModal/FormSection/FormSection';

export const HcoOrganizationalSection = () => {
	const { t } = useTranslation();
	const { hcoFields } = useDcrFields();
	const {
		control,
		watch,
		formState: { dirtyFields }
	} = useFormContext<OrganizationalSectionValuesIn>();
	const section = 'organizational';

	const {
		entityCountryId,
		picklists: {
			hcoTypeOptions,
			statusOptions,
			localHcoTypeOptions,
			hcoOwnershipOptions,
			academicStatusOptions,
			specialtyLocalOptions
		}
	} = useDcrContext();

	const rawLocalSpecs = watch('allSpecLocal_VId');

	const localSpecialties = useMemo(() => {
		return (rawLocalSpecs?.map((val, index) => ({
			id: typeof val === 'object' ? val.id : val,
			name:
				specialtyLocalOptions?.find(
					o => o.id === (val.id ?? val) || o.name === (val.name ?? val)
				)?.name ??
				val.name ??
				val,
			isPrimary: index === 0,
			fieldName: `specialty${index + 1}_V`
		})) ?? []) as MultiPrimaryItem[];
	}, [specialtyLocalOptions, rawLocalSpecs]);

	return (
		<FormSection title={t('Organizational')} id={section}>
			<DcrFormController
				name='hcoType'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField
						label={hcoFields.hcoType.label}
						id='hcoType'
						isRequired
						section={section}
						options={hcoTypeOptions ?? []}
						name={field.name}
						defaultValue={(field.value ?? '') as string}
						indicateError={!!fieldState.error}
						isDirty={fieldState.isDirty}
						handleOnDropdownItemSelect={item => {
							const option = hcoTypeOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			<DcrFormController
				name='hcoTypeLocal_VId'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField
						label={hcoFields.hcoTypeLocal_VId.label}
						section={section}
						id='hcoTypeLocal_VId'
						options={localHcoTypeOptions ?? []}
						defaultValue={field.value}
						indicateError={!!fieldState.error}
						isDirty={fieldState.isDirty}
						handleOnDropdownItemSelect={item => {
							const option = localHcoTypeOptions?.find(
								opt => opt.id === item?.id
							);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			{entityCountryId === 'us' && !!academicStatusOptions?.length && (
				<DcrFormController
					name='academicStatus_VId'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormAutocompleteField
							label={hcoFields.academicStatus_VId.label}
							section={section}
							id='academicStatus_VId'
							options={academicStatusOptions ?? []}
							defaultValue={field.value}
							indicateError={!!fieldState.error}
							isDirty={fieldState.isDirty}
							handleOnDropdownItemSelect={item => {
								const option = academicStatusOptions?.find(
									opt => opt.id === item?.id
								);
								field.onChange(option?.name ?? item?.id ?? '');
							}}
						/>
					)}
				/>
			)}

			<DcrFormController
				name='hcoOwnership_VId'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField
						label={hcoFields.hcoOwnership_VId.label}
						section={section}
						id='hcoOwnership_VId'
						options={hcoOwnershipOptions ?? []}
						defaultValue={field.value}
						indicateError={!!fieldState.error}
						isDirty={fieldState.isDirty}
						handleOnDropdownItemSelect={item => {
							const option = hcoOwnershipOptions?.find(
								opt => opt.id === item?.id
							);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			<MultiPrimaryAutocompleteField
				name='allSpecLocal_VId'
				section={section}
				label={hcoFields.allSpecLocal_VId.label}
				isAutocomplete
				options={specialtyLocalOptions ?? []}
				defaultItems={localSpecialties}
				hidePrimaryButton
				canRemoveItems
				max={2}
			/>

			<DcrFormController
				name='statusCda_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<DropdownItemBase>
						label={hcoFields.statusCda_V.label}
						id='statusCda_V'
						name={field.name}
						section={section}
						options={statusOptions ?? []}
						defaultValue={(field.value ?? '') as string}
						indicateError={!!fieldState.error}
						isDirty={dirtyFields.statusCda_V}
						handleOnDropdownItemSelect={item => {
							const option = statusOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			{/* {(
				<DcrFormController
					name='countPrescriber_VId'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormAutocompleteField
							label={hcoFields.countPrescriber_VId.label}
							section={section}
							id='countPrescriber_VId'
							options={countRangeOptions ?? []}
							defaultValue={field.value}
							indicateError={!!fieldState.error}
							isDirty={fieldState.isDirty}
							handleOnDropdownItemSelect={item => {
								const option = countRangeOptions?.find(
									opt => opt.id === item?.id
								);
								field.onChange(option?.name ?? item?.id ?? '');
							}}
						/>
					)}
				/>
			)}

			{(
				<DcrFormController
					name='countHcp_VId'
					section={section}
					control={control}
					render={({ field, fieldState }) => (
						<FormAutocompleteField
							label={hcoFields.countHcp_VId.label}
							section={section}
							id='countHcp_VId'
							options={countRangeOptions ?? []}
							defaultValue={field.value}
							indicateError={!!fieldState.error}
							isDirty={fieldState.isDirty}
							handleOnDropdownItemSelect={item => {
								const option = countRangeOptions?.find(
									opt => opt.id === item?.id
								);
								field.onChange(option?.name ?? item?.id ?? '');
							}}
						/>
					)}
				/>
			)} */}
		</FormSection>
	);
};
